import { BaseClient } from '../base-client.js';
import { BASE_URLS } from '../../config';
import RequestResponseHelpers from '../../helpers/RequestResponseHelpers';
import Utils from '../../helpers/utils';

class ZephrPublicClient extends BaseClient {
  constructor(config) {
    super(config);
    this.baseUrl = this.baseUrl || BASE_URLS.ZEPHR_PUBLIC_API;
    this.LOGOUT_TYPES = {
      JUST_HERE: 'JUST_HERE',
      THIS_DEVICE: 'THIS_DEVICE',
      OTHER_DEVICES: 'OTHER_DEVICES',
      EVERYWHERE: 'EVERYWHERE'
    };
  }

  account() {
    const url = 'blaize/account';
    return this.get({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  login(username, password) {
    const url = 'blaize/login';
    const data = {
      identifiers: {
        email_address: username
      },
      validators: {
        password
      }
    };
    return this.post({ url, data });
  }

  getProductShareSummary() {
    const url = '/zephr/public/products/v1/shares';
    return this.get({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )

    });
  }

  getProductInviteData(sharingId) {
    const url = `zephr/public/products/v1/shares/invitations/${sharingId}`;
    return this.get({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )

    });
  }

  acceptProductShareInvite(sharingId) {
    const url = `zephr/public/products/v1/shares/invitations/${sharingId}`;
    return this.put({ url });
  }

  deleteProductShare(sharingId) {
    const url = `zephr/public/products/v1/shares/${sharingId}`;
    return this.delete({ url });
  }

  createProductShareInvite(productId, emailAddress, baseUrl, metaData = {}) {
    const url = `zephr/public/products/v1/shares/invitations/emails`;
    const data =
      {
        product_id: productId,
        email_address: emailAddress,
        base_url: baseUrl,
        meta_data: metaData

      };
    return this.post({ url, data });
  }

  ssoLogin() {
    const url = 'blaize/login';
    const data = {
      validators: {
        use_sso: true
      }
    };
    return this.post({ url, data });
  }

  getSsoStatus() {
    const url = 'zephr/public/sso/v1/status';
    const extraInfo = {
      sendSuccessLog: false
    };
    return this.get({ url, extraInfo });
  }

  updateSsoStatusMeta(data) {
    const url = 'zephr/public/sso/v1/status/meta';
    return this.put({
      url,
      data,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        [
          ...RequestResponseHelpers.anyErrorStatus(),
        ]
      )
    });
  }

  // when user is authenticated
  changePassword(oldPassword, newPassword) {
    const url = '/blaize/users/change-password';
    const data = {
      current_validators: {
        password: oldPassword,
      },
      new_validators: {
        password: newPassword,
      }
    };
    return this.post({ url, data });
  }

  // when user is not authenticated
  // resetPasswordStart happens in NHST Auth Client
  validateResetPasswordCode(username, code) {
    const url = `zephr/users/reset/${code}`;
    const data = {
      identifiers: {
        email_address: username
      }
    };
    return this.post({ url, data });
  }

  resetPasswordComplete(username, password, code) {
    const url = `zephr/users/reset/${code}`;
    const data = {
      identifiers: {
        email_address: username
      },
      validators: {
        password
      }
    };
    return this.post({ url, data });
  }

  resetPasswordCompleteUsingLink(password, token) {
    const url = `blaize/users/reset/${token}`;
    const data = {
      validators: {
        password
      }
    };
    return this.post({ url, data });
  }

  updateEmailStart(currentEmail, newEmail, password) {
    const url = '/zephr/users/update-email';
    const data = {
      current_identifiers: {
        email_address: currentEmail
      },
      new_identifiers: {
        email_address: newEmail
      },
      validators: {
        password: password
      }
    };
    return this.post({ url, data });
  }

  updateEmailComplete(currentEmail, code) {
    const url = `/zephr/users/update-email/${code}`;
    const data = {
      current_identifiers: {
        email_address: currentEmail
      }
    };
    return this.post({ url, data });
  }

  logout(type = this.LOGOUT_TYPES.JUST_HERE) {
    const url = 'blaize/logout';
    const data = {
      where: type
    };
    return this.post({ url, data });
  }

  createGiftToken(path) {
    const url = 'plugins/user/public/token-gifts/issue';
    const data = {
      path: path
    };
    return this.post({ url, data });
  }

  profile() {
    const url = 'blaize/profile';
    return this.get({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  tokenGiftState() {
    const url = 'plugins/user/public/token-gifts/state';
    return this.get({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  shouldSendLogToServer(config, isError) {
    return Utils.shouldSendLogToServer(config, isError);
  }

  generateRequestConfigForSendingLogToServer(config) {
    const url = `oneid/auth/logging/${config.level}`;
    const client = this.clientName;
    config.data = config.data || {};
    const data = {
      ...config.data,
      client,
      message: config.message
    };
    const newConfig = {
      ...config,
      url,
      data
    };
    return newConfig;
  }

  register(data) {
    const url = 'blaize/register';
    return this.post({ url, data });
  }

  updateCustomSegments(data) {
    const url = 'plugins/public/custom-segments/update-custom-segments';
    return this.post({ url, data });
  }
}

export { ZephrPublicClient };
